<template>
  <!-- Document Wrapper - Apply basic Tailwind structure -->
  <div id="wrapper" class="flex flex-col min-h-screen"> <!-- Removed clearfix, added flex structure -->
    <Head>
        <!-- Default meta tags, favicons etc. can go here -->
        <!-- These can be overridden by individual pages -->
        <meta http-equiv="content-type" content="text/html; charset=utf-8"/>
        <link rel="icon" type="image/png" href="/favicon-48x48.png" sizes="48x48" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="JuicySMS" />
        <link rel="manifest" href="/site.webmanifest" />
    </Head>

    <!-- Structured Data: Organization + AggregateRating -->
    <JsonLd :data="organizationSchema" />
    <JsonLd v-if="aggregateRatingSchema" :data="aggregateRatingSchema" />

    <!-- Header Component -->
    <AppHeader />

    <!-- Global Notification Bar -->
    <GlobalNotification :message="page.props.globalNotification" />

    <!-- Trustpilot Bar Component -->
    <TrustpilotBar
        v-if="page.component !== 'FigmaWelcome' && page.component !== 'Hdiw'"
        :initial-rating="page.props.trustpilotStats?.rating.toString()"
        :initial-reviews="page.props.trustpilotStats?.reviews.toString()"
     />
     <!-- Note: Props might need adjustment based on TrustpilotBar component implementation -->

     <!-- Language Switcher Component (if needed globally) -->
     <!-- <SwitchLanguageComponent /> -->

    <!-- Main Content Slot -->
    <!-- The @yield('page-title') and @yield('content') from Blade go here -->
    <main id="app"> <!-- Added id="app" from blade -->
      <slot />
    </main>

    <!-- Footer Component -->
    <AppFooter v-if="page.component !== 'FigmaWelcome' && page.component !== 'Hire' && page.component !== 'Hdiw'" />

    <!-- Go To Top Button - Basic Tailwind styling -->
    <div id="gotoTop" @click="scrollToTop" class="fixed bottom-5 right-5 z-50 hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-gray-700 text-white shadow-md transition hover:bg-gray-900">
        <font-awesome-icon :icon="faAngleUp" />
    </div>

    <!-- BlackHatWorld offer popup — the only surface the promo has, now that /bhw is
         a redirect rather than a page. Mounted once here, but self-suppressing: it
         only ever shows on the allowlisted pages in useBhwPopup.js, and only for
         visitors sent here by /bhw or attributed to the BHW thread. -->
    <BhwOfferModal :show="bhwPopupShow" @close="bhwPopupDismiss" @engage="bhwPopupEngage" />

  </div>
</template>

<script setup>
import { Head, usePage } from '@inertiajs/vue3';
import AppHeader from './Partials/AppHeader.vue';
import AppFooter from './Partials/AppFooter.vue';
import GlobalNotification from './Partials/GlobalNotification.vue';
import TrustpilotBar from './Partials/TrustpilotBar.vue';
import JsonLd from '../components/JsonLd.vue';
import BhwOfferModal from '../components/Modals/BhwOfferModal.vue';
import { useBhwPopup } from '../composables/useBhwPopup';
import { useI18n } from 'vue-i18n'; // Import useI18n
import { watch, onMounted, computed } from 'vue';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons'; // Import FA icon

// Helper function to load scripts dynamically and append to body
const loadScript = (src, isAsync = true, isDefer = false, id = null) => {
  return new Promise((resolve, reject) => {
    // Check if script already exists by src or id
    if (document.querySelector(`script[src="${src}"]`) || (id && document.getElementById(id))) {
      console.log(`Script already loaded or loading: ${src}`);
      resolve(); // Assume it's loaded or will load
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    if (id) script.id = id;
    script.async = isAsync;
    script.defer = isDefer; // Set defer attribute if needed
    script.onload = () => {
        console.log(`Script loaded: ${src}`);
        resolve();
    };
    script.onerror = () => {
        console.error(`Failed to load script: ${src}`);
        reject(new Error(`Failed to load script ${src}`));
    };
    document.body.appendChild(script); // Append to body
  });
};

// Access shared props
const page = usePage();
const { locale: i18nLocale, mergeLocaleMessage } = useI18n(); // Get i18n instance methods

// BHW offer popup. Destructured (not `bhwPopup.show`) so the ref unwraps in the
// template. Safe under SSR: the composable registers its router listeners in
// onMounted and touches no browser globals during setup.
const { show: bhwPopupShow, dismiss: bhwPopupDismiss, engage: bhwPopupEngage } = useBhwPopup();

// Structured Data schemas
// The '@id' is a stable identifier for the JuicySMS entity. Every other schema
// block on the site points at it via 'provider'/'publisher' so that search and
// answer engines fuse them into one entity instead of several loose records.
const organizationSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://juicysms.com/#organization',
    'name': 'JuicySMS',
    'alternateName': 'Juicy SMS',
    'url': 'https://juicysms.com',
    'logo': 'https://juicysms.com/images/logo@2x.png',
    'image': 'https://juicysms.com/images/og-default.png',
    'description': 'SMS verification service providing temporary, real mobile phone numbers for receiving one-time verification codes without exposing a personal number.',
    'foundingDate': '2019',
    'slogan': 'Pay only for successfully delivered SMS.',
    'knowsAbout': [
        'SMS verification',
        'One-time password delivery',
        'Temporary phone numbers',
        'Phone number rental',
        'Online account verification',
    ],
    'areaServed': ['US', 'GB', 'NL', 'PH'],
    // Only list profiles that actually exist. An unreachable sameAs weakens
    // entity resolution instead of helping it.
    'sameAs': [
        'https://www.trustpilot.com/review/juicysms.com',
    ],
    'contactPoint': {
        '@type': 'ContactPoint',
        'email': 'support@juicysms.com',
        'contactType': 'customer support',
        'availableLanguage': ['en', 'nl'],
    },
}));

const aggregateRatingSchema = computed(() => {
    const stats = page.props.trustpilotStats;
    if (!stats?.rating || !stats?.reviews) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': 'https://juicysms.com/#service',
        'name': 'JuicySMS SMS Verification Service',
        'description': 'Temporary real mobile numbers for receiving SMS verification codes in the United States, United Kingdom, Netherlands and Philippines. Charged only when a code is delivered.',
        'url': 'https://juicysms.com',
        'image': 'https://juicysms.com/images/og-default.png',
        'brand': { '@id': 'https://juicysms.com/#organization' },
        // A Product with a rating but no offer is incomplete markup. The €0.50
        // floor is the cheapest one-time verification; lowPrice has no upper
        // bound published because per-service pricing varies.
        'offers': {
            '@type': 'AggregateOffer',
            'priceCurrency': 'EUR',
            'lowPrice': '0.50',
            'availability': 'https://schema.org/InStock',
            'url': 'https://juicysms.com',
        },
        'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': String(stats.rating),
            'bestRating': '5',
            'reviewCount': String(stats.reviews),
        },
    };
});

// Watch for locale changes from Inertia props
watch(() => page.props.locale, (newLocale) => {
    console.log("AppLayout: Locale changed via usePage:", newLocale);
    if (newLocale && i18nLocale.value !== newLocale) {
        i18nLocale.value = newLocale; // Update i18n locale
    }
}, { immediate: true }); // Run immediately to set initial locale if different

// Watch for translation changes from Inertia props
watch(() => page.props.translations, (newTranslations) => {
    const currentLocale = i18nLocale.value; // Get current i18n locale
    console.log(`AppLayout: Translations changed via usePage for locale: ${currentLocale}`);
    if (newTranslations && typeof newTranslations === 'object' && Object.keys(newTranslations).length > 0) {
         mergeLocaleMessage(currentLocale, newTranslations); // Merge new translations
         console.log(`AppLayout: Merged new translations for: ${currentLocale}`);
    } else {
         console.log("AppLayout: No new translations or invalid format received.");
    }
}, { deep: true });


// Go To Top Logic (Simple example, might need refinement based on original functions.js)
const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

onMounted(() => {
    // --- Load GoToTop logic ---
    const gotoTopButton = document.getElementById('gotoTop');
    if (gotoTopButton) {
        window.addEventListener('scroll', () => {
            if (window.scrollY > 300) {
                gotoTopButton.style.display = 'block';
            } else {
                gotoTopButton.style.display = 'none';
            }
        });
    }

    // --- Load the consent CMP immediately (not idle-deferred) so the banner and
    // its Consent Mode update fire as early as possible. ---
    // The catch matters: when a content blocker or the network stops this CDN,
    // the unhandled rejection reached Sentry as JUICYSMS-FK (~230/day) with
    // nothing to act on.
    loadScript('//cdn.cookie-script.com/s/b58bba72ca1ebb1c6059386ba87ea83b.js', false, true, 'cookie-script')
        .catch(() => {});

    // --- Load Third-Party Scripts (deferred until browser is idle) ---
    const loadThirdPartyScripts = () => {
        // Initialize inline tracking code first (these queue events until scripts load)

        // JuicyHelp chat init (before GTM so container tags can reference $juicyhelp)
        window.$juicyhelp = window.$juicyhelp || [];
        if (page.props.auth.user) {
            window.$juicyhelp.push(["set", "user:email", page.props.auth.user.email]);
        }

        // GTM dataLayer init (window.dataLayer + window.gtag stubs live in app.blade.php)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({'gtm.start': new Date().getTime(), event:'gtm.js'});

        // Google Analytics gtag init
        gtag('js', new Date());
        gtag('config', 'G-KZGRJR8JQN');

        // Meta Pixel init (fbq queue stub lives in app.blade.php)
        fbq('init', '1248127100800983');
        fbq('track', 'PageView');

        // Reddit Pixel: init already ran synchronously in app.blade.php — it has
        // to, because page components queue their track calls from an onMounted
        // that runs before this one. Only the page view belongs here.
        rdt('track', 'PageVisit');

        // ChatGPT Ads pixel: loader and init live in app.blade.php, same as Reddit.
        oaiq('measure', 'page_viewed', { type: 'contents' });

        // Load all external scripts in parallel (no sequential await waterfall). The
        // Meta/Reddit pixel libraries flush the queues the stubs populated above.
        Promise.allSettled([
            // GTM container GTM-N4F9DK4 via the Stape sGTM first-party loader
            // (juicyt.juicysms.com fronts the container; URL is Stape-issued, do not
            // modify). The dataLayer/gtm.start init above replaces Stape's inline wrapper.
            loadScript('https://juicyt.juicysms.com/eamdqegnb.js?2be=CwpaIiUpQC9WKF4hOlBLF19cSVRXAANcCBsOBggXABJMDQgI', true, false, 'gtm-script'),
            loadScript('//script.crazyegg.com/pages/scripts/0123/0376.js', true, false, 'crazyegg-script'),
            loadScript('https://www.googletagmanager.com/gtag/js?id=G-KZGRJR8JQN', true, false, 'gtag-script'),
            loadScript('https://connect.facebook.net/en_US/fbevents.js', true, false, 'fb-pixel'),
            loadScript(`https://www.redditstatic.com/ads/pixel.js?pixel_id=${page.props.redditPixelId}`, true, false, 'reddit-pixel'),
            loadScript('https://juicyhelp.com/widget.js?key=jh_01kvt06z8nx1nnezm9xs6t9sbw', true, false, 'juicyhelp-script'),
        ]).catch((error) => {
            console.error("Error loading third-party scripts:", error);
        });
    };

    // Defer script loading until the browser is idle. Check the property is actually
    // callable — some Safari versions expose it on window without it being a function,
    // which threw "requestIdleCallback is not a function" (Sentry JUICYSMS-2X).
    if (typeof window.requestIdleCallback === 'function') {
        requestIdleCallback(loadThirdPartyScripts, { timeout: 3000 });
    } else {
        setTimeout(loadThirdPartyScripts, 1500);
    }
});
</script>

<style>
/* Removed old commented-out imports and custom device styles */
/* Make FA icon size adjustable if needed */
#gotoTop svg {
    font-size: 1.25rem; /* Adjust size as needed */
}
</style>
